import { BaseType, BuildComponentTypeDefinition } from "../types/Enums";

/**
 * Builds the display name for a window type: the type name followed by its
 * materials and pane count in parentheses, e.g. "Standard (Trä, Aluminium, 3-glas)".
 * The result is never stored — it's derived from the type definition so it can't
 * drift from the underlying fields. Parts already present in the name are skipped
 * so older types named e.g. "Trä 3-glas" don't repeat themselves.
 */
export const formatWindowTypeName = (
    name: string,
    materialNames: string[],
    paneCount: number | undefined
): string => {
    const lowerName = name.toLocaleLowerCase('sv');
    const parts = [
        ...materialNames,
        ...(paneCount && paneCount > 0 ? [`${paneCount}-glas`] : []),
    ].filter((part) => part && !lowerName.includes(part.toLocaleLowerCase('sv')));

    return parts.length > 0 ? `${name} (${parts.join(', ')})` : name;
};

/**
 * Display name for any component type definition. Window types get their
 * materials and pane count appended; other types return the plain name.
 */
export const buildComponentTypeDisplayName = (
    definition: BuildComponentTypeDefinition,
    translateMaterial: (key: string) => string | undefined
): string => {
    const name = definition.translations?.sv || '';
    if (definition.baseType !== BaseType.Window) return name;

    const rawMaterials = definition.material?.value;
    const materialKeys: string[] = Array.isArray(rawMaterials)
        ? rawMaterials
        : typeof rawMaterials === 'string' ? [rawMaterials] : [];

    return formatWindowTypeName(
        name,
        // Unresolved materials are left out rather than shown as a raw key
        materialKeys.map(translateMaterial).filter((m): m is string => !!m),
        definition.windowProps?.paneCount
    );
};
